import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Chip, Rating, IconButton, Collapse, Divider } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import useStore from '../store';
import { fetchShowPoster } from '../services/showClient';

const ONBOARDING_SHOWS = [
  { id: 'ob-sherlock', title: 'Sherlock', year: 2010 },
  { id: 'ob-inspector-morse', title: 'Inspector Morse', year: 1987 },
  { id: 'ob-broadchurch', title: 'Broadchurch', year: 2013 },
  { id: 'ob-luther', title: 'Luther', year: 2010 },
  { id: 'ob-vera', title: 'Vera', year: 2011 },
  { id: 'ob-endeavour', title: 'Endeavour', year: 2012 },
  { id: 'ob-midsomer-murders', title: 'Midsomer Murders', year: 1997 },
  { id: 'ob-happy-valley', title: 'Happy Valley', year: 2014 },
  { id: 'ob-shetland', title: 'Shetland', year: 2013 },
  { id: 'ob-line-of-duty', title: 'Line of Duty', year: 2012 },
];

const EXTRA_ONBOARDING_SHOWS = [
  { id: 'ob-bodyguard', title: 'Bodyguard', year: 2018 },
  { id: 'ob-killing-eve', title: 'Killing Eve', year: 2018 },
  { id: 'ob-slow-horses', title: 'Slow Horses', year: 2022 },
  { id: 'ob-peaky-blinders', title: 'Peaky Blinders', year: 2013 },
  { id: 'ob-unforgotten', title: 'Unforgotten', year: 2015 },
  { id: 'ob-the-fall', title: 'The Fall', year: 2013 },
  { id: 'ob-blue-lights', title: 'Blue Lights', year: 2023 },
  { id: 'ob-strike', title: 'Strike', year: 2017 },
];

const PREF_OPTIONS = {
  tone: ['Cozy', 'Dark', 'Funny', 'Intense', 'Gritty'],
  era: ['Period piece', 'Contemporary', 'Historical'],
  pacing: ['Slow-burn', 'Fast-paced', 'Character-driven'],
};

const GOLD = '#e8b86d';
const DARK_BG = '#0a0805';
const CARD_BG = '#1c1810';

function StepDots({ step }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 4 }}>
      {[0, 1, 2, 3].map(i => (
        <Box
          key={i}
          sx={{
            width: i === step ? 24 : 8,
            height: 8,
            borderRadius: 4,
            bgcolor: i === step ? GOLD : 'rgba(232,184,109,0.25)',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </Box>
  );
}

function GoldButton({ children, onClick, fullWidth = false, sx = {} }) {
  return (
    <Button
      variant="contained"
      onClick={onClick}
      fullWidth={fullWidth}
      sx={{
        bgcolor: GOLD,
        color: DARK_BG,
        fontWeight: 700,
        textTransform: 'none',
        fontSize: '1rem',
        px: 5,
        py: 1.5,
        borderRadius: 2,
        '&:hover': { bgcolor: '#d4a85e' },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

export default function OnboardingFlow({ onComplete }) {
  const [step, setStep] = useState(0);
  const [preferences, setPreferences] = useState({ tone: [], era: [], pacing: [] });
  const [ratings, setRatings] = useState({});
  const [skipped, setSkipped] = useState({});
  const [posters, setPosters] = useState({});
  const [showExtra, setShowExtra] = useState(false);
  const extraRef = useRef(null);

  const setUserRating = useStore(s => s.setUserRating);
  const shows = useStore(s => s.shows);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  useEffect(() => {
    ONBOARDING_SHOWS.forEach(async (show) => {
      try {
        const url = await fetchShowPoster(show.title, show.year);
        if (url) setPosters(prev => ({ ...prev, [show.id]: url }));
      } catch {
        // falls back to placeholder
      }
    });
  }, []);

  useEffect(() => {
    if (!showExtra) return;
    EXTRA_ONBOARDING_SHOWS.forEach(async (show) => {
      try {
        const url = await fetchShowPoster(show.title, show.year);
        if (url) setPosters(prev => ({ ...prev, [show.id]: url }));
      } catch {
        // falls back to placeholder
      }
    });
    setTimeout(() => extraRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [showExtra]);

  const togglePref = (cat, val) => {
    setPreferences(prev => ({
      ...prev,
      [cat]: prev[cat].includes(val)
        ? prev[cat].filter(v => v !== val)
        : [...prev[cat], val],
    }));
  };

  const handleRate = (show, rating) => {
    if (!rating) return;
    setRatings(prev => ({ ...prev, [show.id]: rating }));
    // Match base library ID if available so the show appears correctly in TrackerScreen
    const baseShow = shows.find(s => s.title.toLowerCase() === show.title.toLowerCase());
    const showId = baseShow ? String(baseShow.id) : show.id;
    setUserRating(showId, {
      showId,
      title: show.title,
      year: show.year,
      poster_path: posters[show.id] || '/poster_placeholder.svg',
      rating,
      notes: '',
      dismissed: false,
    });
  };

  const handleSkip = (showId) => setSkipped(prev => ({ ...prev, [showId]: true }));

  const handleComplete = () => {
    onComplete(preferences);
  };

  const wrapSx = {
    minHeight: '100dvh',
    bgcolor: DARK_BG,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    px: 3,
    pt: 5,
    pb: 5,
  };

  // Step 1 — Welcome
  if (step === 0) {
    return (
      <Box sx={{ ...wrapSx, justifyContent: 'center' }}>
        <StepDots step={step} />
        <SearchIcon sx={{ color: GOLD, fontSize: 56, mb: 3 }} />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: '#f5f0e8',
            textAlign: 'center',
            mb: 2,
            lineHeight: 1.25,
          }}
        >
          Welcome to British Show Butler
        </Typography>
        <Typography
          sx={{
            color: '#9a8870',
            textAlign: 'center',
            fontStyle: 'italic',
            fontSize: '0.95rem',
            lineHeight: 1.8,
            maxWidth: 360,
            mb: 5,
          }}
        >
          "I am your personal British detective concierge. The more you confide in me, the more precisely I can serve you."
        </Typography>
        <GoldButton onClick={() => setStep(1)}>Let's begin</GoldButton>
      </Box>
    );
  }

  // Step 2 — Taste Profile
  if (step === 1) {
    return (
      <Box sx={wrapSx}>
        <StepDots step={step} />
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: '#f5f0e8',
            textAlign: 'center',
            mb: 1,
          }}
        >
          First, tell me your preferences
        </Typography>
        <Typography sx={{ color: '#9a8870', textAlign: 'center', fontSize: '0.85rem', mb: 4 }}>
          Select all that appeal to you.
        </Typography>

        {Object.entries(PREF_OPTIONS).map(([cat, options]) => (
          <Box key={cat} sx={{ width: '100%', maxWidth: 460, mb: 3.5 }}>
            <Typography
              sx={{
                color: '#9a8870',
                fontSize: '0.68rem',
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                mb: 1.5,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {cat}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {options.map(opt => {
                const selected = preferences[cat].includes(opt);
                return (
                  <Chip
                    key={opt}
                    label={opt}
                    onClick={() => togglePref(cat, opt)}
                    sx={{
                      bgcolor: selected ? 'rgba(232,184,109,0.15)' : 'rgba(255,255,255,0.04)',
                      color: selected ? GOLD : '#9a8870',
                      border: selected ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.1)',
                      fontWeight: selected ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: 'rgba(232,184,109,0.1)' },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ))}

        <Box sx={{ flex: 1 }} />
        <GoldButton onClick={() => setStep(2)} fullWidth sx={{ maxWidth: 460, mt: 4 }}>
          Next
        </GoldButton>
      </Box>
    );
  }

  // Step 3 — Rate What You Know
  if (step === 2) {
    return (
      <Box sx={wrapSx}>
        <StepDots step={step} />
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            color: '#f5f0e8',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Now, rate any shows you've already seen
        </Typography>
        <Typography
          sx={{
            color: '#9a8870',
            textAlign: 'center',
            fontSize: '0.82rem',
            mb: 3.5,
            lineHeight: 1.65,
            maxWidth: 420,
          }}
        >
          Tap the stars to rate anything you've watched — even shows you didn't love. Every rating helps the Butler learn your taste and improve future recommendations. Use the skip arrow for shows you haven't seen.
        </Typography>

        {/* Show cards renderer — used for both primary and extra lists */}
        {(() => {
          const renderShowCard = (show) => {
            const isRated = !!ratings[show.id];
            const isSkipped = skipped[show.id] && !isRated;
            const posterSrc = posters[show.id] || '/poster_placeholder.svg';
            return (
              <Box
                key={show.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  bgcolor: CARD_BG,
                  borderRadius: 1.5,
                  border: isRated
                    ? '1px solid rgba(232,184,109,0.3)'
                    : '1px solid rgba(232,184,109,0.08)',
                  opacity: isSkipped ? 0.4 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Box
                    component="img"
                    src={posterSrc}
                    alt={show.title}
                    sx={{ width: 90, height: 135, objectFit: 'cover', borderRadius: 1, display: 'block', bgcolor: '#111' }}
                    onError={e => { e.target.onerror = null; e.target.src = '/poster_placeholder.svg'; }}
                  />
                  {isRated && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.55)', borderRadius: 1 }}>
                      <CheckCircleIcon sx={{ color: GOLD, fontSize: 32 }} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.3, color: '#f5f0e8', mb: 0.25 }}>
                    {show.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9a8870', display: 'block', mb: 1 }}>
                    {show.year}
                  </Typography>
                  <Rating
                    value={ratings[show.id] || 0}
                    precision={0.5}
                    size="small"
                    onChange={(_, val) => handleRate(show, val)}
                    sx={{ '& .MuiRating-iconFilled': { color: GOLD }, '& .MuiRating-iconEmpty': { color: 'rgba(232,184,109,0.25)' } }}
                  />
                </Box>
                {!isRated && (
                  <IconButton size="small" onClick={() => handleSkip(show.id)} title="Haven't seen it"
                    sx={{ color: 'rgba(232,184,109,0.35)', flexShrink: 0, '&:hover': { color: GOLD } }}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          };

          const ratedCount = Object.keys(ratings).length;
          const MIN_RATINGS = 3;
          const needsMore = MIN_RATINGS - ratedCount;

          return (
            <>
              <Box sx={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {ONBOARDING_SHOWS.map(renderShowCard)}
              </Box>

              {/* "Add more shows" expansion */}
              {!showExtra ? (
                <Box sx={{ width: '100%', maxWidth: 460, textAlign: 'center', py: 2, mb: 2 }}>
                  <Divider sx={{ borderColor: 'rgba(232,184,109,0.1)', mb: 2.5 }} />
                  <Typography sx={{ color: '#9a8870', fontSize: '0.82rem', mb: 1.5, fontStyle: 'italic' }}>
                    Haven't watched any of these?
                  </Typography>
                  <Button
                    startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={() => setShowExtra(true)}
                    sx={{
                      color: GOLD,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      border: '1px solid rgba(232,184,109,0.25)',
                      borderRadius: 5,
                      px: 2.5,
                      py: 0.75,
                      '&:hover': { bgcolor: 'rgba(232,184,109,0.06)', borderColor: 'rgba(232,184,109,0.5)' },
                    }}
                  >
                    Let me pull up a few more
                  </Button>
                </Box>
              ) : (
                <Box ref={extraRef} sx={{ width: '100%', maxWidth: 460, mb: 2 }}>
                  <Divider sx={{ borderColor: 'rgba(232,184,109,0.1)', mb: 2.5 }} />
                  <Typography sx={{ color: '#9a8870', fontSize: '0.68rem', letterSpacing: 2.5, textTransform: 'uppercase', textAlign: 'center', mb: 2, fontFamily: '"Inter", sans-serif' }}>
                    A few more from the files
                  </Typography>
                  <Collapse in={showExtra}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {EXTRA_ONBOARDING_SHOWS.map(renderShowCard)}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Bottom CTA — gated behind MIN_RATINGS */}
              <Box sx={{ width: '100%', maxWidth: 460, textAlign: 'center', mt: 1, mb: 1 }}>
                {ratedCount < MIN_RATINGS ? (
                  <Box>
                    <Typography sx={{ color: '#9a8870', fontSize: '0.8rem', fontStyle: 'italic', mb: 2 }}>
                      {needsMore === MIN_RATINGS
                        ? 'Rate a few shows to help me learn your taste.'
                        : `Rate ${needsMore} more show${needsMore !== 1 ? 's' : ''} and I'll be ready to serve.`}
                    </Typography>
                    <GoldButton fullWidth sx={{ maxWidth: 460, opacity: 0.35, pointerEvents: 'none' }}>
                      Let the Butler take it from here
                    </GoldButton>
                  </Box>
                ) : (
                  <Box>
                    <Typography sx={{ color: GOLD, fontSize: '0.8rem', fontStyle: 'italic', mb: 2, opacity: 0.8 }}>
                      Excellent taste. I believe I have quite enough to go on.
                    </Typography>
                    <GoldButton onClick={() => setStep(3)} fullWidth sx={{ maxWidth: 460 }}>
                      Let the Butler take it from here →
                    </GoldButton>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}
      </Box>
    );
  }

  // Step 4 — Ready
  return (
    <Box sx={{ ...wrapSx, justifyContent: 'center' }}>
      <StepDots step={step} />
      <SearchIcon sx={{ color: GOLD, fontSize: 52, mb: 3, opacity: 0.9 }} />
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          color: '#f5f0e8',
          textAlign: 'center',
          mb: 2,
          lineHeight: 1.25,
        }}
      >
        The Butler is ready.
      </Typography>
      <Typography
        sx={{
          color: '#9a8870',
          textAlign: 'center',
          fontStyle: 'italic',
          fontSize: '0.95rem',
          lineHeight: 1.8,
          maxWidth: 360,
          mb: 5,
        }}
      >
        "Your dossier has been prepared. I'll begin assembling recommendations based on your profile."
      </Typography>
      <GoldButton onClick={handleComplete}>Enter</GoldButton>
    </Box>
  );
}
